import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Gamepad2, ArrowRight, Bookmark, CheckCircle2, Heart, MessageCircle, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

export default function VibeShare() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [vibe, setVibe] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [q, setQ] = useState("");
  const [applying, setApplying] = useState(null);
  const [applied, setApplied] = useState(null);
  const [adopting, setAdopting] = useState(false);
  const [adopted, setAdopted] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  useEffect(() => {
    api.get(`/fx/shared-vibe/${code}`)
      .then(({ data }) => setVibe(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get(`/games`).then(({ data }) => setGames(Array.isArray(data) ? data : (data.games || []))).catch(() => {});
    api.get(`/fx/shared-vibe/${code}/comments`).then(({ data }) => setComments(data.comments || [])).catch(() => {});
  }, [code]);

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/fx/shared-vibe/${code}/like`);
      setVibe((v) => (v ? { ...v, liked: data.liked, likes: data.likes } : v));
    } catch { /* ignore */ }
  };

  const postComment = async () => {
    const text = commentText.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/fx/shared-vibe/${code}/comments`, { text });
      setComments((c) => [data.comment, ...c]);
      setCommentText("");
      setVibe((v) => (v ? { ...v, comments_count: (v.comments_count || 0) + 1 } : v));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post that."); }
    finally { setPosting(false); }
  };

  const postReply = async (parentId) => {
    const text = replyText.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/fx/shared-vibe/${code}/comments`, { text, parent_id: parentId });
      setComments((c) => [...c, data.comment]);
      setReplyText(""); setReplyTo(null);
      setVibe((v) => (v ? { ...v, comments_count: (v.comments_count || 0) + 1 } : v));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post that."); }
    finally { setPosting(false); }
  };

  const applyTo = async (gid) => {
    if (applying) return;
    setApplying(gid);
    try {
      await api.post(`/games/${gid}/vibe`, { shared_code: code });
      setApplied(gid);
      toast.success("Vibe applied to your game! 🎨 Open it in the builder to see it.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply that vibe."); }
    finally { setApplying(null); }
  };

  const adopt = async () => {
    if (adopting) return;
    setAdopting(true);
    try {
      await api.post(`/fx/shared-vibe/${code}/adopt`);
      setAdopted(true);
      toast.success("Saved to your My Vibes — reuse it on any game! ✨");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save that vibe."); }
    finally { setAdopting(false); }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  if (notFound || !vibe) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="vibe-share-notfound">
        <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[hsl(var(--secondary))] mx-auto mb-4"><Sparkles className="w-6 h-6 text-[hsl(var(--muted-foreground))]" /></div>
        <h1 className="font-display text-xl font-bold">This vibe link is invalid or expired</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Ask your friend for a fresh link, or make your own vibe in any game's Quick FX tray.</p>
        <Button onClick={() => navigate("/app/games")} className="rounded-full mt-5" data-testid="vibe-share-mygames">Go to my games</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="vibe-share">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-14 h-14 rounded-2xl grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#a855f7] shrink-0 text-3xl">{vibe.emoji || "🎨"}</div>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Shared vibe</p>
          <h1 className="font-display text-xl font-extrabold leading-tight" data-testid="vibe-share-name">{vibe.name}</h1>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">by {vibe.author_name}</p>
        </div>
        <button onClick={toggleLike} data-testid="vibe-share-like-btn"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-1.5 text-sm font-semibold hover:border-rose-400/60 transition-colors shrink-0">
          <Heart className={`w-4 h-4 ${vibe.liked ? "fill-rose-500 text-rose-500" : ""}`} /> {vibe.likes || 0}
        </button>
        <a href="/app/vibes" data-testid="vibe-share-gallery-link" className="text-xs font-semibold text-[hsl(var(--primary))] hover:underline shrink-0">Browse gallery →</a>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-6">
        <div className="flex items-start gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
          <p className="text-sm">One tap stacks this creator's weather, particles, colour grade &amp; time-of-day onto your game.</p>
        </div>
        {Array.isArray(vibe.fx) && vibe.fx.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="vibe-share-fx">
            {vibe.fx.map((f, i) => <span key={i} className="text-[11px] rounded-full bg-[hsl(var(--secondary))] px-2.5 py-1 capitalize">{f}</span>)}
          </div>
        )}
        <button onClick={adopt} disabled={adopting || adopted} data-testid="vibe-share-adopt-btn"
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-semibold hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-colors disabled:opacity-60">
          {adopting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : adopted ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Bookmark className="w-3.5 h-3.5" />}
          {adopted ? "Saved to My Vibes" : "Save to My Vibes"}
        </button>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
        <div className="flex items-center gap-2 mb-1 text-sm font-semibold"><Gamepad2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Apply this vibe to one of your games</div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">It saves as a new version you can undo — your game, your history.</p>
        {games.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">You don't have a game yet — create one first, then come back to this link.</p>
            <Button onClick={() => navigate("/app/create")} className="rounded-full mt-4" data-testid="vibe-share-create">Create a game</Button>
          </div>
        ) : (
          <>
            {games.length > 8 && (
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your games…"
                data-testid="vibe-share-search"
                className="w-full mb-3 rounded-xl border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--primary))]/60" />
            )}
            <div className="grid sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
              {games.filter((g) => (g.title || "").toLowerCase().includes(q.trim().toLowerCase())).map((g) => {
                const isApplied = applied === g.id;
                return (
                  <button key={g.id} onClick={() => applyTo(g.id)} disabled={!!applying} data-testid={`vibe-share-game-${g.id}`}
                    className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] px-3 py-2.5 text-left hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-colors group disabled:opacity-60">
                    <span className="text-xl shrink-0">{g.cover_emoji || g.config?.player?.emoji || "🎮"}</span>
                    <span className="min-w-0 flex-1 text-sm font-medium truncate">{g.title}</span>
                    {applying === g.id ? <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--primary))] shrink-0" />
                      : isApplied ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      : <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] shrink-0" />}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Comments */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mt-4" data-testid="vibe-share-comments">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><MessageCircle className="w-4 h-4 text-[hsl(var(--primary))]" /> Comments {comments.length > 0 && <span className="text-[hsl(var(--muted-foreground))] font-normal">({comments.length})</span>}</div>
        <div className="flex gap-2 mb-4">
          <input value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postComment()}
            placeholder="Say something nice…" maxLength={280} data-testid="vibe-comment-input"
            className="flex-1 rounded-xl border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--primary))]/60" />
          <button onClick={postComment} disabled={posting || !commentText.trim()} data-testid="vibe-comment-submit"
            className="inline-flex items-center gap-1 rounded-xl bg-[hsl(var(--primary))] text-white text-sm font-semibold px-3 py-2 hover:opacity-90 disabled:opacity-50">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        {comments.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Be the first to comment on this vibe.</p>
        ) : (
          <div className="space-y-3">
            {comments.filter((c) => !c.parent_id).map((c) => {
              const replies = comments.filter((r) => r.parent_id === c.id).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
              return (
                <div key={c.id} data-testid={`vibe-comment-${c.id}`}>
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full bg-[hsl(var(--secondary))] grid place-items-center text-xs font-bold shrink-0">{(c.author_name || "?")[0]}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold">{c.author_name}</div>
                      <div className="text-sm text-[hsl(var(--foreground))] break-words">{c.text}</div>
                      <button onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyText(""); }} data-testid={`vibe-reply-btn-${c.id}`}
                        className="text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline mt-0.5">
                        {replyTo === c.id ? "Cancel" : "Reply"}
                      </button>
                    </div>
                  </div>
                  {(replies.length > 0 || replyTo === c.id) && (
                    <div className="ml-9 mt-2 space-y-2 border-l border-[hsl(var(--border))] pl-3">
                      {replies.map((r) => (
                        <div key={r.id} className="flex gap-2" data-testid={`vibe-comment-${r.id}`}>
                          <div className="w-6 h-6 rounded-full bg-[hsl(var(--secondary))] grid place-items-center text-[10px] font-bold shrink-0">{(r.author_name || "?")[0]}</div>
                          <div className="min-w-0">
                            <div className="text-[11px] font-semibold">{r.author_name}</div>
                            <div className="text-sm break-words">{r.text}</div>
                          </div>
                        </div>
                      ))}
                      {replyTo === c.id && (
                        <div className="flex gap-2">
                          <input value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postReply(c.id)}
                            placeholder={`Reply to ${c.author_name}…`} maxLength={280} autoFocus data-testid={`vibe-reply-input-${c.id}`}
                            className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-transparent px-2.5 py-1.5 text-xs focus:outline-none focus:border-[hsl(var(--primary))]/60" />
                          <button onClick={() => postReply(c.id)} disabled={posting || !replyText.trim()} data-testid={`vibe-reply-submit-${c.id}`}
                            className="inline-flex items-center rounded-lg bg-[hsl(var(--primary))] text-white text-xs font-semibold px-2.5 py-1.5 hover:opacity-90 disabled:opacity-50">
                            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
